import { useMutation } from '@tanstack/react-query';

import { AUTH_GRAVITEE_ENABLED, HOST_MB, PASS_URL } from '@/lib/constants';
import { apiRequest, getApiErrorMessage } from '@/lib/api/client';
import { apiV2Public, postData } from '@/lib/api/client-v2';
import { DATE_ISO, formatDate } from '@/lib/dates';
import { notify } from '@/lib/notify';
import { initiateGraviteeLogin } from '@/lib/auth/gravitee';
import { PUBLIC_ENDPOINTS } from '@/types/api/endpoints';

import { useLogin } from '@/hooks/auth/use-auth';

/*
 * Parcours de récupération d'accès, avant connexion : identifiant oublié, mot de passe oublié,
 * définition d'un nouveau mot de passe.
 */

/** Routes d'authentification du Pass, servies à la racine de son domaine, hors de toute version d'API. */
const passAuthUrl = (path: string) => `${new URL(PASS_URL).origin}${path}`;

export interface ForgotEmailValues {
    nom?: string;
    belair_id?: string;
    date_naissance?: Date | null;
    email?: string;
}

/** « Quel identifiant utiliser ? » : retrouve le compte à partir du nom, du n° client et de la naissance. */
export const useForgotEmail = () =>
    useMutation({
        mutationFn: ({ nom, belair_id, date_naissance, email }: ForgotEmailValues) =>
            postData<null>(
                PUBLIC_ENDPOINTS.customers,
                { nom, belair_id, date_naissance: formatDate(date_naissance, DATE_ISO), email, owner_belair_id: HOST_MB },
                undefined,
                apiV2Public,
            ),
        onSuccess: () => {
            void notify({
                title: 'Félicitations !',
                icon: 'success',
                html: `
                <p>Vous avez reçu un email pour définir un nouveau mot de passe.</p>
                <p>Si vous n'avez rien reçu, consultez votre boîte de spams ou vos courriers indésirables.</p>
            `,
                confirmButtonText: "J'ai compris",
            });
        },
        onError: () => {
            void notify({
                title: 'Attention !',
                icon: 'error',
                html: `<p>Nous ne retrouvons pas votre compte avec les informations saisies.</p>`,
                confirmButtonText: "J'ai compris",
            });
        },
    });

export interface ForgotPasswordValues {
    email: string;
    callback_url: string;
}

/** Demande d'un lien de (ré)initialisation du mot de passe. */
export const useForgotPassword = () =>
    useMutation({
        mutationFn: ({ email, callback_url }: ForgotPasswordValues) =>
            apiRequest({
                url: passAuthUrl('/auth/password/forgot'),
                method: 'POST',
                data: { email, provider: 'customers', owner_belair_id: HOST_MB, callback_url },
            }),
        onSuccess: () => {
            void notify({
                html: `
                <p>Si cet email existe, vous recevrez un lien de réinitialisation.</p>
                <p>Si vous n'avez rien reçu, consultez votre boîte de spams ou vos courriers indésirables.</p>`,
                icon: 'success',
                title: 'Email envoyé !',
                confirmButtonText: "J'ai compris",
            });
        },
        onError: () => {
            // Message générique pour éviter l'énumération d'utilisateurs
            void notify({
                html: `
                <p>Une erreur est survenue lors de l'envoi de l'email.</p>
                <p>Veuillez réessayer ou <a href='/login/oubli-email'><u>utiliser votre numéro client.</u></a></p>
            `,
                icon: 'error',
                title: 'Erreur',
                confirmButtonText: "J'ai compris",
            });
        },
    });

export interface ResetPasswordValues {
    email: string;
    password: string;
    password_confirmation: string;
    token?: string | null;
    owner_belair_id?: number;
}

/**
 * Définition d'un nouveau mot de passe à partir du lien reçu par email.
 *
 * Après succès : en MFA, redirection vers Gravitee AM pour une connexion complète (email + mot de
 * passe + SMS) ; sinon, connexion automatique avec les identifiants saisis.
 */
export const useResetPassword = () => {
    const login = useLogin();

    const resetPassword = useMutation({
        mutationFn: (form: ResetPasswordValues) =>
            apiRequest({
                url: passAuthUrl('/auth/password/reset'),
                method: 'POST',
                data: { ...form, provider: 'customers', owner_belair_id: form.owner_belair_id || HOST_MB },
            }),
        onSuccess: (_data, form) => {
            if (AUTH_GRAVITEE_ENABLED) {
                void initiateGraviteeLogin();
            } else {
                login.mutate({ username: form.email, password: form.password });
            }
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Le lien est peut-être expiré ou invalide.';

            void notify({
                html: `<p>${errorMessage}</p>`,
                icon: 'error',
                title: 'Erreur',
                confirmButtonText: "J'ai compris",
            });
        },
    });

    return { resetPassword, login };
};
